import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLocale } from "../i18n";

export const PAGE_SIZE = 24;
const KEYS = {
  make: "mk", model: "md", year_from: "yf", year_to: "yt", price_min: "pn", price_max: "px", mileage_max: "km",
  body_type: "bt", fuel: "fu", transmission: "tr", drivetrain: "dt", sort: "s",
  aspiration: "as", displacement_min: "cn", displacement_max: "cx", exterior_color: "ec", market: "mt", equipment: "eq", mileage_min: "kn",
};
export const EMPTY_FILTERS = Object.fromEntries(Object.keys(KEYS).map((k) => [k, ""]));

export const activeFilterCount = (f) =>
  Object.entries(f).filter(([k, v]) => v && k !== "sort" && k !== "scope").length;

export const toApiParams = (f) => {
  const params = {};
  Object.entries(f).forEach(([k, v]) => {
    if (v) params[k] = v;
  });
  if (f.scope === "sold" && !f.sort) params.sort = "sold_recent";  // sold page default: recently sold
  return params;
};

export const useInventoryQuery = () => {
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => Object.fromEntries(Object.entries(KEYS).map(([k, s]) => [k, sp.get(s) || ""])), [sp]);
  const page = Math.max(1, Math.min(10, parseInt(sp.get("p") || "1", 10) || 1));

  const setFilters = useCallback(
    (next) => {
      const p = new URLSearchParams();
      Object.entries(KEYS).forEach(([k, s]) => next[k] && p.set(s, next[k]));
      setSp(p, { preventScrollReset: true });
    },
    [setSp]
  );

  const setPage = useCallback(
    (n) => {
      setSp(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (n > 1) p.set("p", String(n));
          else p.delete("p");
          return p;
        },
        { replace: true, preventScrollReset: true }
      );
    },
    [setSp]
  );

  return { filters, page, setFilters, setPage };
};

const cache = new Map();

export const useInventory = (filters, page, setPage) => {
  const lang = useLocale();
  const key = JSON.stringify({ ...filters, lang });
  const pageRef = useRef(page);
  pageRef.current = page;
  const [entry, setEntry] = useState(() => cache.get(key) || null);
  const [status, setStatus] = useState(() => (cache.get(key) ? "ready" : "loading"));
  const [loadingMore, setLoadingMore] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const cached = cache.get(key);
    if (cached && cached.loadedPages >= pageRef.current) {
      setEntry(cached);
      setStatus("ready");
      return undefined;
    }
    let active = true;
    const pages = pageRef.current;
    setStatus("loading");
    api
      .get("/vehicles", { params: { ...toApiParams(JSON.parse(key)), page: 1, page_size: PAGE_SIZE * pages } })
      .then(({ data }) => {
        if (!active) return;
        const next = { items: data.items, total: data.total, loadedPages: pages };
        cache.set(key, next);
        setEntry(next);
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [key, reload]);

  const loadMore = useCallback(async () => {
    if (!entry) return;
    const nextPage = entry.loadedPages + 1;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/vehicles", { params: { ...toApiParams(filters), lang, page: nextPage, page_size: PAGE_SIZE } });
      const next = { items: [...entry.items, ...data.items], total: data.total, loadedPages: nextPage };
      cache.set(key, next);
      setEntry(next);
      setPage(nextPage);
    } catch {
      setStatus("error");
    } finally {
      setLoadingMore(false);
    }
  }, [entry, filters, key, lang, setPage]);

  const retry = useCallback(() => {
    cache.delete(key);
    setReload((n) => n + 1);
  }, [key]);

  return { entry, status, loadingMore, loadMore, retry };
};
